import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Minus, Trash2, AlertTriangle, BookmarkPlus, X } from "lucide-react";
import { SIZES, SERVICES, MAX_WEIGHT, sizeIdx, minSizeForWeight, cap } from "./constants";
import { toast } from "sonner";

const serviceWarning = (v) => {
  if (v === "express") {
    toast.warning("Priority selected", {
      description: "Your job jumps the queue in the town of delivery — it's dropped off ahead of Standard jobs on that run. The Priority rate applies.",
      duration: 9000, style: { background: "#f97316", color: "#fff", border: "1px solid #ea580c" }, className: "service-toast-priority", classNames: { description: "!text-white" },
    });
  } else if (v === "sameday") {
    toast.error("Urgent selected — here's what happens if you proceed", {
      description: "1. A dedicated driver is assigned to your job straight away. 2. They collect as soon as humanly possible. 3. They drive straight to your drop-off with no other stops. 4. You can follow the driver live on the tracking page. The Urgent rate applies.",
      duration: 12000, style: { background: "#dc2626", color: "#fff", border: "1px solid #b91c1c" }, className: "service-toast-urgent", classNames: { description: "!text-white" },
    });
  }
};

export default function ItemsCard({ service, onServiceChange, savedItems, items, updateItem, addItem, removeItem, saveItem, addSavedToItems, deleteSaved }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-lg">Items</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label>Service level</Label>
          <Select value={service} onValueChange={(v) => { onServiceChange(v); serviceWarning(v); }}>
            <SelectTrigger data-testid="service-level-select" className="h-auto min-h-10 whitespace-normal text-left [&>span]:line-clamp-2"><SelectValue /></SelectTrigger>
            <SelectContent className="max-w-[var(--radix-select-trigger-width)]">{SERVICES.map((s) => (
              <SelectItem key={s.v} value={s.v} data-testid={`service-${s.v}`} className="whitespace-normal">
                <span className="font-semibold">{s.l}</span> <span className="text-muted-foreground">— {s.d}</span>
              </SelectItem>
            ))}</SelectContent>
          </Select>
        </div>

        {savedItems.length > 0 && (
          <div className="rounded-md border border-border bg-secondary/40 p-3" data-testid="frequent-items">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><BookmarkPlus className="h-3.5 w-3.5 text-kilted-gold" /> Frequent items — tap to add</p>
            <div className="flex flex-wrap gap-2">
              {savedItems.map((s) => (
                <div key={s.id} className="group flex items-center gap-1 rounded-full border border-kilted-green/30 bg-white pl-3 pr-1 py-1 text-xs" data-testid={`saved-item-${s.id}`}>
                  <button type="button" onClick={() => addSavedToItems(s)} data-testid={`add-saved-item-${s.id}`} className="flex items-center gap-1 font-medium text-kilted-green hover:underline">
                    <Plus className="h-3 w-3" /> {s.label} <span className="text-muted-foreground">· {cap(s.size)} {s.weight_kg}kg</span>
                  </button>
                  <button type="button" onClick={() => deleteSaved(s.id)} data-testid={`delete-saved-item-${s.id}`} className="rounded-full p-0.5 text-muted-foreground hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {items.map((it, idx) => {
          const minS = minSizeForWeight(it.weight);
          const overweight = (parseFloat(it.weight) || 0) > MAX_WEIGHT;
          return (
            <div key={it.uid} className="space-y-4 rounded-lg border border-border p-4" data-testid={`item-row-${idx}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Item {idx + 1}</p>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => saveItem(it)} data-testid={`save-item-${idx}`} title="Save as frequent item" className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-kilted-green">
                    <BookmarkPlus className="h-4 w-4" /> Save
                  </button>
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`} className="text-muted-foreground transition-colors hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Weight (kg)</Label>
                  <Input type="number" min="0.1" step="0.1" value={it.weight} onChange={(e) => updateItem(idx, { weight: e.target.value })} data-testid={`item-weight-input-${idx}`} />
                  {overweight && (
                    <p className="flex items-center gap-1 text-xs font-medium text-amber-700" data-testid={`item-overweight-${idx}`}>
                      <AlertTriangle className="h-3 w-3" /> Over the 22.5kg limit — call us to arrange heavy freight.
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Parcel size</Label>
                  <Select value={it.size} onValueChange={(v) => updateItem(idx, { size: v })}>
                    <SelectTrigger data-testid={`item-size-select-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SIZES.map((s) => (
                        <SelectItem key={s.v} value={s.v} disabled={sizeIdx(s.v) < sizeIdx(minS)} data-testid={`item-${idx}-size-${s.v}`}>{s.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground" data-testid={`item-size-hint-${idx}`}>{it.sizeTouched ? <>Size chosen manually — must stay at least <span className="font-semibold">{cap(minS)}</span> for this weight.</> : <>Size set automatically from weight (<span className="font-semibold">{cap(minS)}</span>). Pick a larger size for bulky items.</>}</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Quantity</Label>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="icon" className="h-9 w-9" disabled={it.quantity <= 1} onClick={() => updateItem(idx, { quantity: Math.max(1, it.quantity - 1) })} data-testid={`item-qty-decrease-${idx}`}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-10 text-center font-heading text-lg font-bold" data-testid={`item-qty-value-${idx}`}>{it.quantity}</span>
                    <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={() => updateItem(idx, { quantity: it.quantity + 1 })} data-testid={`item-qty-increase-${idx}`}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Description</Label>
                  <Textarea value={it.desc} onChange={(e) => updateItem(idx, { desc: e.target.value })} placeholder="What's inside? (optional)" data-testid={`item-description-input-${idx}`} />
                </div>
              </div>
            </div>
          );
        })}

        <Button type="button" variant="outline" onClick={addItem} data-testid="add-item-button" className="w-full gap-2 border-dashed">
          <Plus className="h-4 w-4" /> Add another item type
        </Button>

        <div className="rounded-md border border-border bg-secondary/40 p-3" data-testid="sizing-guide">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Item sizing guide</p>
          <ul className="space-y-1 text-xs text-muted-foreground">
            <li><span className="font-semibold text-foreground">Small</span> — under 30cm in any dimension · under 12.5kg</li>
            <li><span className="font-semibold text-foreground">Medium</span> — 30–50cm in any dimension · under 16.5kg</li>
            <li><span className="font-semibold text-foreground">Large</span> — over 50cm in any dimension · under 22.5kg</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
